import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, Loader2, CreditCard } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/hooks/use-toast";
import {
  createPlan,
  updatePlan,
  type Plan,
} from "@/services/plansService";

interface PlanFormProps {
  onBack: () => void;
  onSave: () => void;
  editingPlan: Plan | null;
}

interface FormData {
  name: string;
  monthly_call_limit: string;
  monthly_token_limit: string;
  excess_rate_per_1m_tokens: string;
  alert_threshold_1_percent: string;
  alert_threshold_2_percent: string;
}

export const PlanForm = ({ onBack, onSave, editingPlan }: PlanFormProps) => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    monthly_call_limit: "",
    monthly_token_limit: "",
    excess_rate_per_1m_tokens: "",
    alert_threshold_1_percent: "80",
    alert_threshold_2_percent: "100",
  });
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (editingPlan) {
      setFormData({
        name: editingPlan.name,
        monthly_call_limit: String(editingPlan.monthly_call_limit),
        monthly_token_limit: String(editingPlan.monthly_token_limit),
        excess_rate_per_1m_tokens: String(editingPlan.excess_rate_per_1m_tokens),
        alert_threshold_1_percent:
          editingPlan.alert_threshold_1_percent == null
            ? ""
            : String(editingPlan.alert_threshold_1_percent),
        alert_threshold_2_percent:
          editingPlan.alert_threshold_2_percent == null
            ? ""
            : String(editingPlan.alert_threshold_2_percent),
      });
    }
  }, [editingPlan]);

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast({
        title: "Erro de Validação",
        description: "Nome do plano é obrigatório",
        variant: "destructive",
      });
      return;
    }

    if (formData.name.trim().toLowerCase() === "enterprise") {
      toast({
        title: "Erro de Validação",
        description:
          "O nome 'Enterprise' é reservado e não pode ser usado neste catálogo",
        variant: "destructive",
      });
      return;
    }

    const monthlyCallLimit = Number(formData.monthly_call_limit);
    const monthlyTokenLimit = Number(formData.monthly_token_limit);
    const excessRate = Number(formData.excess_rate_per_1m_tokens);

    if (
      !Number.isInteger(monthlyCallLimit) ||
      monthlyCallLimit < 0 ||
      !Number.isInteger(monthlyTokenLimit) ||
      monthlyTokenLimit < 0 ||
      Number.isNaN(excessRate) ||
      excessRate < 0
    ) {
      toast({
        title: "Erro de Validação",
        description:
          "Informe limites inteiros não negativos e taxa de excedente válida",
        variant: "destructive",
      });
      return;
    }

    const parseThreshold = (value: string) =>
      value.trim() === "" ? null : Number(value);
    const threshold1 = parseThreshold(formData.alert_threshold_1_percent);
    const threshold2 = parseThreshold(formData.alert_threshold_2_percent);
    const thresholdInvalid = (value: number | null) =>
      value !== null &&
      (!Number.isInteger(value) || value < 1 || value > 100);

    if (thresholdInvalid(threshold1) || thresholdInvalid(threshold2)) {
      toast({
        title: "Erro de Validação",
        description: "Limite 1 e Limite 2 devem ser inteiros entre 1 e 100",
        variant: "destructive",
      });
      return;
    }

    if (threshold1 !== null && threshold2 !== null && threshold2 <= threshold1) {
      toast({
        title: "Erro de Validação",
        description: "O Limite 2 (%) deve ser maior que o Limite 1 (%)",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        monthly_call_limit: monthlyCallLimit,
        monthly_token_limit: monthlyTokenLimit,
        excess_rate_per_1m_tokens: excessRate,
        alert_threshold_1_percent: threshold1,
        alert_threshold_2_percent: threshold2,
      };

      const response = editingPlan
        ? await updatePlan(editingPlan.id, payload)
        : await createPlan(payload);

      if (response.success) {
        toast({
          title: "Sucesso",
          description: editingPlan
            ? "Plano atualizado com sucesso"
            : "Plano criado com sucesso",
        });
        onSave();
      } else {
        throw new Error(response.error?.message || "Erro ao salvar plano");
      }
    } catch (error) {
      toast({
        title: "Erro",
        description:
          error instanceof Error ? error.message : "Erro ao salvar plano",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      <PageHeader
        icon={CreditCard}
        title={editingPlan ? "Editar Plano" : "Novo Plano"}
        description="Catálogo fixo de planos comerciais"
        onBack={onBack}
      />

      <main className="container mx-auto px-6 py-6 max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle>Dados do plano</CardTitle>
            <CardDescription>
              Limites mensais e taxa de excedente por milhão de tokens
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Ex.: Start"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="monthly_call_limit">
                Limite mensal de acionamentos
              </Label>
              <Input
                id="monthly_call_limit"
                type="number"
                min={0}
                step={1}
                value={formData.monthly_call_limit}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    monthly_call_limit: e.target.value,
                  })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="monthly_token_limit">
                Limite mensal de tokens
              </Label>
              <Input
                id="monthly_token_limit"
                type="number"
                min={0}
                step={1}
                value={formData.monthly_token_limit}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    monthly_token_limit: e.target.value,
                  })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="excess_rate_per_1m_tokens">
                Taxa de excedente (R$ / 1M tokens)
              </Label>
              <Input
                id="excess_rate_per_1m_tokens"
                type="number"
                min={0}
                step="0.01"
                value={formData.excess_rate_per_1m_tokens}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    excess_rate_per_1m_tokens: e.target.value,
                  })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="alert_threshold_1_percent">Limite 1 (%)</Label>
              <Input
                id="alert_threshold_1_percent"
                type="number"
                min={1}
                max={100}
                step={1}
                placeholder="Ex.: 80"
                value={formData.alert_threshold_1_percent}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    alert_threshold_1_percent: e.target.value,
                  })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="alert_threshold_2_percent">Limite 2 (%)</Label>
              <Input
                id="alert_threshold_2_percent"
                type="number"
                min={1}
                max={100}
                step={1}
                placeholder="Ex.: 100"
                value={formData.alert_threshold_2_percent}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    alert_threshold_2_percent: e.target.value,
                  })
                }
              />
            </div>

            <div className="flex justify-end pt-4">
              <Button onClick={handleSubmit} disabled={saving}>
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Salvar
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};
